import { useMembers } from '@/features/members/public';
import { useTeamTimeOffAbsences } from './useTeamTimeOffAbsences';
import type { TimeOffDateRangeQueryParams } from '../types';

const MEMBERS_PAGE_SIZE = 200;

export interface TeamMemberBalanceRow {
  userId: string;
  displayName: string;
  takenDays: number;
}

/**
 * Per-member "days taken" for a single absence type in a date range, derived
 * client-side from `GET /time-off/team` (the only team-wide absence read
 * that exists). There is no admin endpoint exposing `assigned`/`available`
 * balance for another member — `TimeOffBalanceDto` is only served via
 * `GET /time-off/me/balance` (self-only) — so this hook cannot compute those
 * two columns at all; callers must render them as "not calculable". See the
 * "Saldo por persona" section of `tasks/current-story.md` for the endpoint
 * requested from backend to close this gap.
 */
export function useTeamMemberBalances(range: TimeOffDateRangeQueryParams, typeId: string) {
  const {
    data: members,
    isLoading: membersLoading,
    isError: membersError,
  } = useMembers('', 0, MEMBERS_PAGE_SIZE);
  const {
    data: absences,
    isLoading: absencesLoading,
    isError: absencesError,
  } = useTeamTimeOffAbsences(range);

  const rows: TeamMemberBalanceRow[] = (members ?? []).map((member) => {
    const takenDays = (absences ?? [])
      .filter(
        (request) =>
          request.requesterUserId === member.userId &&
          request.typeId === typeId &&
          request.status === 'Approved',
      )
      .reduce(
        (sum, request) =>
          sum + (request.days ?? []).filter((day) => day.status !== 'Cancelled').length,
        0,
      );
    return { userId: member.userId, displayName: member.displayName, takenDays };
  });

  return {
    rows,
    isLoading: membersLoading || absencesLoading,
    isError: membersError || absencesError,
  };
}
